/**
 * S4 (C3 reconciled build plan — docs/architecture/REALTIME-STREAMING.md):
 * relay envelope + bounded dedup for the every-tab raw-frame relay.
 *
 * The LEADER's socket wrapper relays each RAW inbound WS frame (never the
 * applied cache update) over the TabCoordinator's existing WS_MESSAGE channel.
 * Every relay is enveloped with:
 *   - originTabId  — which tab relayed it (diagnostics; NOT the dedup key),
 *   - generation   — the leader's connection generation,
 *   - relayId      — unique per relay post,
 *   - scope        — the authenticated user identity; followers REJECT
 *                    relays whose scope ≠ their own session (a tab logged in
 *                    as another user on the same origin must not cross-feed),
 *   - kind         — "frame" (data) or "hb" (leader-socket liveness, S5).
 *
 * DEDUP is CONTENT-based and bounded: in a dual-leader race BOTH leaders
 * relay the same backend frame with different relayIds/originTabIds, so only
 * the frame content identifies the duplicate. Backend frames carry
 * microsecond timestamps, so identical content ⇒ the same event. A hash
 * collision (rare) drops one frame — recovered by the trailing
 * reconciliation/TTL, which is the right trade against unbounded memory.
 */

export const RELAY_DEDUP_CAPACITY = 512;

export type RelayEnvelopeKind = "frame" | "hb";

export interface RelayEnvelope {
  v: 1;
  kind: RelayEnvelopeKind;
  originTabId: string;
  generation: number;
  relayId: string;
  scope: string;
  frame?: string;
  ts: number;
}

interface EnvelopeFields {
  originTabId: string;
  generation: number;
  scope: string;
}

let relayCounter = 0;
const nextRelayId = (originTabId: string): string =>
  `${originTabId}:${++relayCounter}:${Math.random().toString(36).slice(2, 8)}`;

export const createRelayFrameEnvelope = (
  fields: EnvelopeFields & { frame: string },
): string =>
  JSON.stringify({
    v: 1,
    kind: "frame",
    originTabId: fields.originTabId,
    generation: fields.generation,
    relayId: nextRelayId(fields.originTabId),
    scope: fields.scope,
    frame: fields.frame,
    ts: Date.now(),
  } satisfies RelayEnvelope);

export const createRelayHeartbeatEnvelope = (fields: EnvelopeFields): string =>
  JSON.stringify({
    v: 1,
    kind: "hb",
    originTabId: fields.originTabId,
    generation: fields.generation,
    relayId: nextRelayId(fields.originTabId),
    scope: fields.scope,
    ts: Date.now(),
  } satisfies RelayEnvelope);

export const parseRelayEnvelope = (raw: string): RelayEnvelope | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<RelayEnvelope> | null;
    if (
      !parsed ||
      parsed.v !== 1 ||
      (parsed.kind !== "frame" && parsed.kind !== "hb") ||
      typeof parsed.originTabId !== "string" ||
      typeof parsed.generation !== "number" ||
      typeof parsed.relayId !== "string" ||
      typeof parsed.scope !== "string" ||
      (parsed.kind === "frame" && typeof parsed.frame !== "string")
    ) {
      return null;
    }
    return parsed as RelayEnvelope;
  } catch {
    return null;
  }
};

/**
 * Stable user identity for relay scoping. Prefers the numeric/string user id;
 * falls back to email. Returns null when the session carries no identity —
 * callers must FAIL CLOSED (no relay) rather than trust unscoped frames.
 */
export const deriveRelaySessionScope = (
  session: { user?: { id?: unknown; email?: unknown } } | null | undefined,
): string | null => {
  const id = session?.user?.id;
  if (typeof id === "number" || (typeof id === "string" && id.length > 0)) {
    return String(id);
  }
  const email = session?.user?.email;
  if (typeof email === "string" && email.length > 0) {
    return email;
  }
  return null;
};

/** djb2 over the frame string — fast, good spread for JSON text. */
const hashFrame = (frame: string): string => {
  let hash = 5381;
  for (let i = 0; i < frame.length; i += 1) {
    hash = ((hash << 5) + hash + frame.charCodeAt(i)) | 0;
  }
  return `${frame.length}:${hash}`;
};

/** Bounded FIFO content-dedup (dual-leader duplicate suppression). */
export class RelayDeduper {
  private readonly seenKeys = new Set<string>();
  private readonly fifo: string[] = [];

  /** Returns true when this frame content was already seen (drop it). */
  seen(frame: string): boolean {
    const key = hashFrame(frame);
    if (this.seenKeys.has(key)) {
      return true;
    }
    this.seenKeys.add(key);
    this.fifo.push(key);
    if (this.fifo.length > RELAY_DEDUP_CAPACITY) {
      const evicted = this.fifo.shift();
      if (evicted !== undefined) {
        this.seenKeys.delete(evicted);
      }
    }
    return false;
  }
}
